"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Smartphone } from "lucide-react";
import { navLinks } from "@/lib/data";
import { usePathname } from "next/navigation";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const pathname = usePathname();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);

    onScroll();
    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${scrolled
          ? "border-b border-hairline bg-white/90 shadow-sm backdrop-blur-xl"
          : "border-b border-transparent bg-white/60 backdrop-blur-md"
          }`}
      >
        <nav className="container-px mx-auto flex h-20 max-w-7xl items-center justify-between">

          {!open && (
            <Link href="/" className="flex items-center gap-2 focus-ring" aria-label="Mobilink home">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl text-white">
                <img src="/logo/logo1.png" alt="Mobilink Logo" className="h-9 w-9" />
              </span>
              <span className="text-lg font-bold tracking-tight text-ink">Mobilink</span>
            </Link>
          )}

          {/* Desktop Menu */}
          <ul className="hidden items-center gap-8 lg:flex">
            {navLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className={`relative text-sm font-medium transition-colors ${pathname === link.href
                    ? "text-primary"
                    : "text-ink/80 hover:text-primary"
                    }`}
                >
                  {link.label}

                  {pathname === link.href && (
                    <span className="absolute -bottom-2 left-0 h-0.5 w-full rounded-full bg-primary" />
                  )}
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop Buttons */}
          <div className="hidden items-center gap-3 lg:flex">
            {/* <Link href="/contact" className="btn-secondary">
              Download App
            </Link> */}

            <Link href="/contact" className="btn-primary">
              Contact Us
            </Link>
          </div>

          {/* Mobile Toggle */}
          {!open && (
            <button
              onClick={() => setOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-hairline lg:hidden"
              aria-label="Open Menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
        </nav>
      </header>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {open && (
          <>
            {/* Overlay */}
            <motion.div
              className="fixed inset-0 z-50 bg-black/40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />

            {/* Sidebar */}
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3 }}
              className="fixed right-0 top-0 z-[60] flex h-full w-80 max-w-[85vw] flex-col bg-white shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b border-hairline px-6 py-5">
                <Link href="/" className="flex items-center gap-2 focus-ring" aria-label="Mobilink home">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl text-white">
                    <img src="/logo/logo1.png" alt="Mobilink Logo" className="h-9 w-9" />
                  </span>
                  <span className="text-lg font-bold tracking-tight text-ink">Mobilink</span>
                </Link>

                <button
                  onClick={() => setOpen(false)}
                  className="rounded-full p-2 hover:bg-accent"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Navigation */}
              <nav className="flex-1 px-6 py-6">
                <ul className="space-y-2">
                  {navLinks.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        onClick={() => setOpen(false)}
                        className={`block rounded-xl px-4 py-3 text-base font-medium transition ${pathname === link.href
                          ? "bg-primary text-white"
                          : "text-ink hover:bg-accent hover:text-primary"
                          }`}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Footer Buttons */}
              <div className="border-t border-hairline p-6">
                <div className="flex flex-col gap-3">
                  {/* <Link
                    href="/contact"
                    onClick={() => setOpen(false)}
                    className="btn-secondary w-full text-center"
                  >
                    Download App
                  </Link> */}

                  <Link
                    href="/contact"
                    onClick={() => setOpen(false)}
                    className="btn-primary w-full text-center"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}